import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useCommerceAPI } from '~/features/commerce-api/contexts';
import {
    PRODUCT_DATA_GC_TIME,
    PRODUCT_DATA_STALE_TIME,
} from '~/features/product-details/constants';
import {
    getVariantFamilyKey,
    productQueryKey,
    sortIDsForQueryKeys,
} from '~/features/product-details/utilities';
import { SalesforceProduct } from '~/lib/salesforce/types';

const PRODUCT_BATCH_LIMIT = 24;
const PREFETCH_DELAY = 1500;

/**
 * Prefetches the primary product's variants to allow smoother navigating when switching to them.
 * We will add a bit of a "delay" to allow our main components to complete loading first.
 */
export const useVariantPrefetch = (product: SalesforceProduct | undefined, currency: string) => {
    const { shopperProducts } = useCommerceAPI();
    const queryClient = useQueryClient();

    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        const timeout = window.setTimeout(() => setIsEnabled(true), PREFETCH_DELAY);

        return () => window.clearTimeout(timeout);
    }, []);

    const familyKey = product ? getVariantFamilyKey(product) : '';

    const siblingIds = useMemo(() => {
        if (!product?.c_variants?.length) return [];

        const ids = product.c_variants
            .map((variant) => variant.productId)
            .filter((id): id is string => !!id && id !== product.id);

        // The product on screen is already cached, and re-seeding it would swap the object
        // identity the whole page renders from, so it stays out of the batch.
        return (sortIDsForQueryKeys([...new Set(ids)]) as string[]).slice(0, PRODUCT_BATCH_LIMIT);
    }, [product?.id]);

    useQuery({
        queryKey: ['Salesforce', 'ProductFamily', familyKey],
        queryFn: async () => {
            const response = await shopperProducts?.getProducts({
                parameters: {
                    ids: siblingIds.join(','),
                    allImages: true,
                    currency,
                },
            });

            const products = (response?.data ?? []) as SalesforceProduct[];

            // We will save the sibling product data to their own query key for later fetching.
            products.forEach((entry) => queryClient.setQueryData(productQueryKey(entry.id), entry));

            return products;
        },
        enabled: isEnabled && !!siblingIds.length && !!shopperProducts,
        staleTime: PRODUCT_DATA_STALE_TIME,
        gcTime: PRODUCT_DATA_GC_TIME,
        refetchOnWindowFocus: false,
        retry: false,
    });
};
